import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Edit, Trash2, LogOut, Search, Eye, X, FileText, Package, LayoutDashboard, ChevronLeft, ChevronRight } from "lucide-react";
import "../styles/admin-dashboard.css";

const AdminDashboard = () => {
  const navigate = useNavigate();

  // 1. Khởi tạo tab từ localStorage (để F5 không bị mất tab)
  const [activeTab, setActiveTab] = useState(() => {
      return localStorage.getItem("adminActiveTab") || "blogs";
  });
  
  // Dữ liệu
  const [blogs, setBlogs] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Phân trang (Dùng chung cho cả 2 tab)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Số lượng item mỗi trang

  // Modal Preview
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) navigate("/admin/login");
  }, [navigate]);

  // Lưu tab khi thay đổi
  useEffect(() => {
      localStorage.setItem("adminActiveTab", activeTab);
  }, [activeTab]);

  // Fetch dữ liệu
  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setSearchTerm("");
    setCurrentPage(1); // Reset về trang 1 khi chuyển tab
    try {
      if (activeTab === "blogs") {
        const res = await axios.get("https://dht.api.mekongzone.com.vn/api/tintuc");
        setBlogs(res.data.sort((a, b) => new Date(b.ngaydang) - new Date(a.ngaydang)));
      } else {
        const res = await axios.get("https://dht.api.mekongzone.com.vn/api/products");
        setProducts(res.data); 
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, type) => {
    const confirmMsg = type === 'blog' ? "Xóa bài viết này?" : "Xóa sản phẩm này?";
    if (!window.confirm(confirmMsg)) return;

    try {
      if (type === 'blog') {
        await axios.delete(`https://dht.api.mekongzone.com.vn/api/tintuc/${id}`);
        setBlogs(blogs.filter(item => item.id !== id));
      } else {
        await axios.delete(`https://dht.api.mekongzone.com.vn/api/products/${id}`);
        setProducts(products.filter(item => item.masp !== id));
      }
      alert("Đã xóa thành công!");
    } catch (error) {
      alert("Xóa thất bại: " + error.message);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("adminActiveTab"); 
    navigate("/admin/login");
  };

  // Preview Blog
  const handleOpenPreview = (blog) => {
    setPreviewData(blog);
    setShowPreview(true);
  };

  const renderPreviewContent = (blog) => {
    if (!blog) return "";
    let html = blog.noidung || "";
    let imgIndex = 0;
    html = html.replace(/{{IMAGE_PLACEHOLDER\|?(.*?)}}/g, (match, mode) => {
        const imgUrl = blog.hinhanhs && blog.hinhanhs[imgIndex] ? blog.hinhanhs[imgIndex] : "";
        imgIndex++;
        return imgUrl ? `<img src="${imgUrl}" style="max-width:100%; border-radius:8px; margin:10px 0" />` : "";
    });
    return html;
  };

  // --- LOGIC LỌC & PHÂN TRANG (ÁP DỤNG CHO CẢ 2) ---
  
  // 1. Lọc dữ liệu theo tab và từ khóa tìm kiếm
  const filteredData = activeTab === "blogs" 
    ? blogs.filter(b => b.tieude.toLowerCase().includes(searchTerm.toLowerCase()))
    : products.filter(p => p.tensp.toLowerCase().includes(searchTerm.toLowerCase()));

  // 2. Tính toán phân trang
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  
  // Cắt dữ liệu cho trang hiện tại (Áp dụng cho cả Blog và Product)
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="admin-layout">
      {/* SIDEBAR */}
      <aside className="sidebar">
        <div className="sidebar-header">
            <LayoutDashboard size={24} /> Admin Panel
        </div>
        <div className="sidebar-menu">
            <button className={`menu-item ${activeTab === 'blogs' ? 'active' : ''}`} onClick={() => setActiveTab('blogs')}>
                <FileText size={20} /> Bài Viết
            </button>
            <button className={`menu-item ${activeTab === 'products' ? 'active' : ''}`} onClick={() => setActiveTab('products')}>
                <Package size={20} /> Sản Phẩm
            </button>
        </div>
        <div style={{marginTop: 'auto', padding: 20}}>
             <button onClick={handleLogout} className="btn btn-danger" style={{width: '100%', justifyContent:'center'}}>
                <LogOut size={18} /> Đăng xuất
             </button>
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="main-content">
        <div className="admin-header">
            <h2 className="admin-title">{activeTab === 'blogs' ? 'Quản Lý Bài Viết' : 'Quản Lý Sản Phẩm'}</h2>
            <Link to={activeTab === 'blogs' ? "/admin/add-blog" : "/admin/add-product"} className="btn btn-primary">
                <Plus size={18} /> {activeTab === 'blogs' ? 'Viết bài mới' : 'Thêm sản phẩm'}
            </Link>
        </div>

        <div className="search-wrapper">
            <Search size={18} className="search-icon" />
            <input 
                type="text" className="search-input" 
                placeholder={activeTab === 'blogs' ? "Tìm tiêu đề bài viết..." : "Tìm tên sản phẩm..."}
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            />
        </div>

        <div className="table-card">
            {loading ? <div style={{padding:40, textAlign:'center'}}>Đang tải dữ liệu...</div> : (
                <table className="custom-table">
                    <thead>
                        {activeTab === 'blogs' ? (
                            <tr><th>#</th><th>Hình</th><th>Tiêu đề</th><th>Danh mục</th><th>Ngày đăng</th><th style={{textAlign:'right'}}>Hành động</th></tr>
                        ) : (
                            <tr><th>ID</th><th>Hình</th><th>Tên Sản Phẩm</th><th>Trọng lượng</th><th>Mô tả / Thành phần</th><th style={{textAlign:'right'}}>Hành động</th></tr>
                        )}
                    </thead>
                    <tbody>
                        {currentItems.map((item, index) => (
                            <tr key={activeTab === 'blogs' ? item.id : item.masp} style={{maxHeight:"95px"}}>
                                {/* Render Blog Row */}
                                {activeTab === 'blogs' && (
                                    <>
                                        <td>{indexOfFirstItem + index + 1}</td> {/* Số thứ tự tăng dần theo trang */}
                                        <td>{item.hinhanhs?.[0] ? <img src={item.hinhanhs[0]} className="thumb-img" alt=""/> : <div className="no-img"/>}</td>
                                        <td><div style={{fontWeight:'bold'}}>{item.tieude}</div></td>
                                        <td><span style={{background:'#e0f2fe', color:'#0369a1', padding:'2px 8px', borderRadius:10, fontSize:12}}>{item.danhmuc}</span></td>
                                        <td>{new Date(item.ngaydang).toLocaleDateString("vi-VN")}</td>
                                        <td style={{textAlign:'right'}}>
                                            <button className="action-btn view" onClick={() => handleOpenPreview(item)}><Eye size={18}/></button>
                                            <Link to={`/admin/edit-blog/${item.id}`} className="action-btn edit"><Edit size={18}/></Link>
                                            <button className="action-btn delete" onClick={() => handleDelete(item.id, 'blog')}><Trash2 size={18}/></button>
                                        </td>
                                    </>
                                )}
                                
                                {/* Render Product Row */}
                                {activeTab === 'products' && (
                                    <>
                                        <td><b>{item.masp}</b></td>
                                        <td>{item.hinhanh ? <img src={item.hinhanh} className="thumb-img" alt=""/> : <div className="no-img"/>}</td>
                                        <td>
                                            <div style={{fontWeight:'bold', color:'#2563eb'}}>{item.tensp}</div>
                                            <div style={{fontSize:'0.8rem', color:'#64748b'}}>{item.dongSanPham}</div>
                                        </td>
                                        <td>{item.khoiluong} {item.donViTinh}</td>
                                        <td>
                                            <div style={{maxWidth:250, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontSize:'0.85rem', color:'#666'}}>
                                                {item.mota || item.thanhPhan}
                                            </div>
                                        </td>
                                        <td style={{textAlign:'right'}}>
                                            <Link to={`/admin/edit-product/${item.masp}`} className="action-btn edit"><Edit size={18}/></Link>
                                            <button className="action-btn delete" onClick={() => handleDelete(item.masp, 'product')}><Trash2 size={18}/></button>
                                        </td>
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>

        {/* Pagination Controls - Hiện cho cả 2 tab nếu > 1 trang */}
        {totalPages > 1 && (
            <div className="pagination">
                <button className="page-btn" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}><ChevronLeft size={16}/></button>
                {Array.from({length: totalPages}, (_, i) => (
                    <button key={i+1} onClick={() => paginate(i+1)} className={`page-btn ${currentPage === i+1 ? 'active' : ''}`}>
                        {i+1}
                    </button>
                ))}
                <button className="page-btn" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}><ChevronRight size={16}/></button>
            </div>
        )}
      </main>

      {/* Modal Preview Blog */}
      {showPreview && previewData && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header"><h3>{previewData.tieude}</h3><button className="close-btn" onClick={() => setShowPreview(false)}><X size={20}/></button></div>
                <div className="modal-body"><div dangerouslySetInnerHTML={{ __html: renderPreviewContent(previewData) }} /></div>
            </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;